import { Injectable, effect, signal, computed } from '@angular/core';
import { Subject } from 'rxjs';

declare const window: Window & typeof globalThis;
declare const document: Document;

export interface layoutConfig {
  preset?: string;
  primary?: string;
  surface?: string | undefined | null;
  darkTheme?: boolean;
  menuMode?: string;
}

interface LayoutState {
  staticMenuDesktopInactive?: boolean;
  overlayMenuActive?: boolean;
  configSidebarVisible?: boolean;
  staticMenuMobileActive?: boolean;
  menuHoverActive?: boolean;
}

interface MenuChangeEvent {
  key: string;
  routeEvent?: boolean;
}

type DocumentWithViewTransition = Document & {
  startViewTransition?: (callback: () => void) => {
    ready: Promise<undefined>;
    finished: Promise<undefined>;
    updateCallbackDone: Promise<undefined>;
    skipTransition: () => void;
  };
};

@Injectable({
  providedIn: 'root'
})
export class LayoutService {
  _config: layoutConfig = {
    preset: 'Aura',
    primary: 'emerald',
    surface: null,
    darkTheme: false,
    menuMode: 'static'
  };

  _state: LayoutState = {
    staticMenuDesktopInactive: false,
    overlayMenuActive: false,
    configSidebarVisible: false,
    staticMenuMobileActive: false,
    menuHoverActive: false
  };

  layoutConfig = signal<layoutConfig>(this._config);

  layoutState = signal<LayoutState>(this._state);

  private configUpdate = new Subject<layoutConfig>();

  private overlayOpen = new Subject<void>();

  private menuSource = new Subject<MenuChangeEvent>();

  private resetSource = new Subject<boolean>();

  menuSource$ = this.menuSource.asObservable();

  resetSource$ = this.resetSource.asObservable();

  configUpdate$ = this.configUpdate.asObservable();

  overlayOpen$ = this.overlayOpen.asObservable();

  theme = computed(() => (this.layoutConfig()?.darkTheme ? 'light' : 'dark'));

  isSidebarActive = computed(() => this.layoutState().overlayMenuActive || this.layoutState().staticMenuMobileActive);

  isDarkTheme = computed(() => this.layoutConfig().darkTheme);

  getPrimary = computed(() => this.layoutConfig().primary);

  getSurface = computed(() => this.layoutConfig().surface);

  isOverlay = computed(() => this.layoutConfig().menuMode === 'overlay');

  transitionComplete = signal<boolean>(false);

  private initialized = false;

  constructor() {
    effect(() => {
      const config = this.layoutConfig();

      if (config) {
        this.onConfigUpdate();
      }
    });

    effect(() => {
      const config = this.layoutConfig();

      if (!this.initialized || !config) {
        this.initialized = true;

        return;
      }

      this.handleDarkModeTransition(config);
    });
  }

  private handleDarkModeTransition(config: layoutConfig): void {
    const doc = document as DocumentWithViewTransition;
    const hasViewTransition = doc.startViewTransition !== undefined;

    if (hasViewTransition) {
      this.startViewTransition(config);
    } else {
      this.toggleDarkMode(config);
      this.onTransitionEnd();
    }
  }

  private startViewTransition(config: layoutConfig): void {
    const doc = document as DocumentWithViewTransition;

    if (doc.startViewTransition) {
      const transition = doc.startViewTransition(() => {
        this.toggleDarkMode(config);
      });

      transition.ready
        .then(() => {
          this.onTransitionEnd();
        })
        .catch(() => {});
    }
  }

  toggleDarkMode(config?: layoutConfig): void {
    const _config = config || this.layoutConfig();

    if (_config.darkTheme) {
      document.documentElement.classList.add('app-dark');
    } else {
      document.documentElement.classList.remove('app-dark');
    }
  }

  private onTransitionEnd() {
    this.transitionComplete.set(true);

    setTimeout(() => {
      this.transitionComplete.set(false);
    });
  }

  onMenuToggle() {
    if (this.isOverlay()) {
      this.layoutState.update((prev) => ({ ...prev, overlayMenuActive: !this.layoutState().overlayMenuActive }));

      if (this.layoutState().overlayMenuActive) {
        this.overlayOpen.next();
      }
    }

    if (this.isDesktop()) {
      this.layoutState.update((prev) => ({
        ...prev,

        staticMenuDesktopInactive: !this.layoutState().staticMenuDesktopInactive
      }));
    } else {
      this.layoutState.update((prev) => ({
        ...prev,

        staticMenuMobileActive: !this.layoutState().staticMenuMobileActive
      }));

      if (this.layoutState().staticMenuMobileActive) {
        this.overlayOpen.next();
      }
    }
  }

  isDesktop() {
    return window.innerWidth > 991;
  }

  isMobile() {
    return !this.isDesktop();
  }

  onConfigUpdate() {
    this._config = { ...this.layoutConfig() };

    this.configUpdate.next(this.layoutConfig());
  }

  onMenuStateChange(event: MenuChangeEvent) {
    this.menuSource.next(event);
  }

  reset() {
    this.resetSource.next(true);
  }

  private getComputedStyle(element: Element): CSSStyleDeclaration {
    return window.getComputedStyle(element);
  }

  calculateRemToPx(rem: number): number {
    return rem * parseFloat(this.getComputedStyle(document.documentElement).fontSize);
  }

  calculateTableHeight() {
    const windowHeight = window.innerHeight;
    const headerHeight = document.querySelector('.layout-topbar')?.clientHeight || 0;
    const tableHeaderHeight = document.querySelector('.p-datatable-thead')?.clientHeight || 0;
    const tableFooterHeight = document.querySelector('.p-datatable-tfoot')?.clientHeight || 0;
    const footerHeight = document.querySelector('.layout-footer')?.clientHeight || 0;
    const padding = this.calculateRemToPx(12);
    const pageTitle = document.querySelector('.page-title')?.clientHeight || 0;

    return `${windowHeight - headerHeight - tableHeaderHeight - tableFooterHeight - footerHeight - padding - pageTitle}px`;
  }

  calculateTableHeightAddGroup() {
    const windowHeight = window.innerHeight;
    const headerHeight = document.querySelector('.layout-topbar')?.clientHeight || 0;
    const addGroupCardHeight = document.querySelector('.add-group-card')?.clientHeight || 0;
    const tableHeaderHeight = document.querySelector('.p-datatable-thead')?.clientHeight || 0;
    const tableFooterHeight = document.querySelector('.p-datatable-tfoot')?.clientHeight || 0;
    const footerHeight = document.querySelector('.layout-footer')?.clientHeight || 0;
    const padding = this.calculateRemToPx(9.5);

    return `${windowHeight - headerHeight - addGroupCardHeight - tableHeaderHeight - tableFooterHeight - footerHeight - padding}px`;
  }
}
